// ============================================================
// historySnapshots - what an undo snapshot holds, and what restoring
// one does to the roster. Pure: no React, no I/O, no clock (the
// caller hands in the watermark).
//
// Split out of useHistory, which composes these with its refs and the
// shared-history transport (historyShared.ts).
// ============================================================

import deepClone from '../utils/deepClone';
import type { Combatant, CombatState } from '../engine/roster/types';
import type { HistorySnapshot, TurnTrackerState } from '../types';
import type { PhaseFrame } from '../engine/phase/types';

/**
 * The roster's STATE half, its narrative log lengths, the watermark and
 * the tracker - never a sheet, so a sheet edit survives an undo.
 *
 * `cloneStates` deep-copies the states: the PRE-action snapshot must, as
 * the roster is about to change under it; the post-action one need not.
 * The tracker is always copied. Key order is fixed (states, counts,
 * watermark, tracker, direction) - the push transaction compares serialized
 * entries. `direction` (the GM's frame) rides along: it is restored with the
 * snapshot but is not part of what makes two snapshots the same.
 */
export function snapshotOf(
  roster: readonly Combatant[],
  turnTracker: TurnTrackerState | undefined,
  watermark: number,
  cloneStates: boolean,
  direction?: PhaseFrame | null,
  cohesion?: unknown,
): HistorySnapshot {
  const snap: HistorySnapshot = {
    states: Object.fromEntries(roster.map((c) => [c.id, cloneStates ? deepClone(c.state) : c.state])),
    narrativeLogCounts: Object.fromEntries(roster.map((c) => [c.id, c.narrativeLog.length])),
    narrativeWatermark: watermark,
  };
  if (turnTracker) snap.turnTracker = deepClone(turnTracker);
  // Appended last, and only when known: an entry written before v8.430.0 has none.
  if (direction) snap.direction = { ...direction };
  // The pools when known (Squad Mode): null or undefined is "not loaded", never written as an empty pool.
  if (cohesion !== null && cohesion !== undefined) snap.cohesion = deepClone(cohesion);
  return snap;
}

/** What an entry is ABOUT, for comparing two - its watermark and baseline flag are not content. */
const contentOf = (s: HistorySnapshot) =>
  JSON.stringify({
    states: s.states,
    narrativeLogCounts: s.narrativeLogCounts,
    turnTracker: s.turnTracker,
    cohesion: s.cohesion,
  });

/** Whether two snapshots differ in their Cohesion pools: an undo that puts only those back still does something. */
export const cohesionDiffers = (snapshot: HistorySnapshot, now: unknown): boolean =>
  snapshot.cohesion !== undefined && JSON.stringify(snapshot.cohesion) !== JSON.stringify(now ?? null);

/** Whether two snapshots describe the same table - states, log lengths, tracker and the Cohesion pools. */
export function sameContent(a: HistorySnapshot, b: HistorySnapshot): boolean {
  return contentOf(a) === contentOf(b);
}

/**
 * The roster with the snapshot's states put back.
 *
 * Only combatants that currently exist and that the snapshot knows -
 * never add or remove anyone, and never touch a sheet. An undo restores
 * OLD content, so its snapshot carries an OLD write clock; replaying that
 * verbatim would be refused as a stale echo by every listener (v7.5.0).
 * The restore has to be the NEWEST write, so the clock advances past
 * whatever is live even as the content goes back.
 */
export function clockedRestore(roster: readonly Combatant[], snapshot: HistorySnapshot): Combatant[] {
  return roster
    .filter((c) => snapshot.states[c.id] || snapshot.narrativeLogCounts?.[c.id] !== undefined)
    .map((c) => {
      const restoredState = snapshot.states[c.id];
      if (!restoredState) return c;
      return { ...c, state: { ...restoredState, rev: (c.state.rev ?? 0) + 1 } };
    });
}

/** Whether this restored combatant differs from who it was - its state, or its log's length. */
function differs(before: Combatant | undefined, after: Combatant, count: number | undefined): boolean {
  if (before?.state !== after.state) return true;
  return count !== undefined && before !== undefined && before.narrativeLog.length !== count;
}

/** Whether restoring would change anything at all - an undo that changes nothing does nothing. */
export function restoreChangesAnything(
  roster: readonly Combatant[],
  restored: readonly Combatant[],
  snapshot: HistorySnapshot,
): boolean {
  return restored.some((c) =>
    differs(
      roster.find((x) => x.id === c.id),
      c,
      snapshot.narrativeLogCounts?.[c.id],
    ),
  );
}

/**
 * The states to restore: the ones we CLOCKED, not the raw snapshot ones.
 *
 * `restored` carries the advanced `rev`; `snapshot.states` still holds the
 * clock from when it was captured. Applying the raw ones locally while
 * writing the clocked ones to the server leaves this client behind its
 * own write - it then counts up from a lower rev and every peer correctly
 * refuses everything it sends from then on. That is the post-undo sync latch.
 */
export function clockedStates(restored: readonly Combatant[], snapshot: HistorySnapshot): Record<string, CombatState> {
  return Object.fromEntries(restored.filter((c) => snapshot.states[c.id]).map((c) => [c.id, c.state]));
}
